/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { ContentType } from 'client-code/grid-guide';
import { PreviewType, ScreenType } from 'client-code/previews';

import {
    GRID_TIMEBAR_REFRESH_TIME,
    GUIDE_NETWORK_CALL_WAIT_DURATION,
    IDLE_GRID_GUIDE_DURATION,
    PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    PRIMARY_BRANDING_LOGO_WIDTH_1080P,
    TURBO_SCROLL_ANIMATION_DURATION,
} from '../../config';
import { AppUtils } from '../../core/AppUtils';
import { DeviceModel } from '../../core/device';
import { NFREvents } from '../../core/enums';
import { PreviewError } from '../../core/error';
import { VodInteractor } from '../../core/interactors';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { OperationsLogger, VuLogger } from '../../core/logging';
import { Log } from '../../core/logging/Log';
import { NFRLoggings } from '../../core/logging/NFRLoggings';
import {
    CursorManager,
    KeyManager,
    LocationUrls,
    ScreenControllerTypes,
} from '../../core/navigation';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { TScreen } from '../../core/navigation/types';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TGridGuideRow, TGuideCell } from '../../core/types';
import { TOfferPreview } from '../../core/types/TOfferPreview';
import { UIOrchestrator, UIOrchestratorState } from '../../core/uiorchestrator';
import { DateTime } from '../../core/utils';
import { isJumpChannelCcbSwitchEnabled } from '../../core/utils/ccb/CcbSwitchHelper';
import { UniqueIdentifier } from '../../core/utils/UniqueIdentifier';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import {
    resetGridGuideChannelsAction,
    resetGridGuideRowsAction,
    setGridGuideOfferPreviewDataAction,
} from '../../store/guide/guide.actions';
import {
    guideHideSpinner,
    guideSetCaretInvisible,
    guideShowSpinner,
    loadGridGuide,
    loadGridGuideFutureRows,
    loadGridGuidePastRows,
    loadGridGuideRows,
    refreshGridGuide,
} from '../../store/guide/guide.reducers';
import { setHorizontalPositionAction } from '../../store/guide/guidehighlight/guidehighlight.actions';
import { setHorizontalPosition } from '../../store/guide/guidehighlight/guidehighlight.reducers';
import { evaluateCurrentHorizontalSessionIndex } from '../../store/guide/guidenavigation/guidekeyoperations.reducers';
import { fetchAndUpdateChannels } from '../../store/guide/turboscroll/turboscroll.reducers';
import { setChannelOptions } from '../../store/overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import {
    clearOverlayTaskController,
    hideOverlayDialog,
} from '../../store/overlaydialog/overlaydialog.reducers';
import { getRecordingsStatus } from '../../store/recordings/recordings.reducers';
import { setScreenTaskController } from '../../store/screens/screens.reducers';
import { hideSpinner } from '../../store/spinner/spinner.reducers';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { showWhisper } from '../../store/whisper/whisper.reducer';
import { Clock } from '../Clock';
import { ClockTypes } from '../Clock/ClockTypes';
import { AntiburnOsd, Mousetrap } from '../common';
import { useScreenController } from '../hooks';
import { ImageLoader } from '../ImageLoader';
import { ImageLoaderTypes } from '../ImageLoader/ImageLoaderTypes';
import { NoNetworkOverlay } from '../NoNetworkOverlay';
import { SidebarNavigationMenu } from '../SidebarNavigationMenu';
import { Spinner } from '../VideoPlayerUI/Spinner';
import { Whisper } from '../Whisper';
import { WhisperTypes } from '../Whisper/WhisperTypes';
import { GridGuideFooter } from './GridGuideFooter';
import { GridGuideHeader } from './GridGuideHeader';
import { InteractiveLayer } from './InteractiveLayer';
import { MainGuide } from './MainGuide';
import { PastGuide } from './PastGuide';
import { PosterImage } from './PosterImage';
import { PreviewPane } from './PreviewPane';
import { TurboScroll } from './TurboScroll';

import './Guide.scss';

/* eslint-disable react-hooks/exhaustive-deps */

const Guide: FunctionComponent = (): JSX.Element => {
    const TAG: string = 'GrideGuide';
    useScreenController<TScreen>({
        identifier: TAG,
        properties: {
            url: LocationUrls.GUIDE,
            screenController: ScreenControllerTypes.GUIDE,
            taskController: ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS,
        },
    });
    const [posterImageUrl, setPosterImageUrl] = useState('');
    const [isAdultPosterImage, setIsAdultPosterImage] = useState(false);
    const [mOfferPreview, setMOfferPreview] = useState({});
    const mShouldHideMetadataRef = useRef<boolean>(true);
    const isScreenActive = React.useRef(false);
    const enablePosterImage = React.useRef(false);

    const gridGuide = useTivoSelector((state: RootState) => state.gridGuide);
    const guideHighlight = useTivoSelector((state: RootState) => state.guideHighlight);
    const isAntiburnOsdActive = useTivoSelector((state: RootState) => state.antiburnOsd.isActive);
    const isOverlayDialogActive = useTivoSelector(
        (state: RootState) => state.overlayDialog.isOverlayDialogActive,
    );
    const channelsTypeIndex = useTivoSelector(
        (state: RootState) => state.channelOptionsOverlay.channelsTypeIndex,
    );
    const categoriesTypeIndex = useTivoSelector(
        (state: RootState) => state.channelOptionsOverlay.categoriesTypeIndex,
    );
    const isTurboScrollActive = useTivoSelector(
        (state: RootState) => state.turboScroll.isTurboScrollActive,
    );
    const isGuidePageScrollActive = useTivoSelector(
        (state: RootState) => state.turboScroll.isGuidePageScrollActive,
    );
    const pcState = useTivoSelector((state: RootState) => state.parentalControls.pcState);
    const isSpinnerActive = useTivoSelector((state: RootState) => state.spinner.isSpinnerActive);
    const maxAge = useTivoSelector((state: RootState) => state.recordingsStatus.maxAge);
    const recordingsStatusMap = useTivoSelector(
        (state: RootState) => state.recordingsStatus.recordingsStatusMap,
    );
    const visibilityState = useTivoSelector(
        (state: RootState) => state.visibilityEvents.visibilityState,
    );

    const {
        guideRows,
        LIMIT,
        channels,
        pastGuideRows,
        currentTimestamp,
        nextTimestamp,
        isPastGuideMode,
        isChannelsServiceFail,
        futureGuideRows,
        startTime,
        isSpinnerActive: isGuideSpinnerActive,
        isGuideRowsServiceFail,
    } = gridGuide;
    const { horizontalPosition, verticalPosition, horizontalSession, verticalPageCount } =
        guideHighlight;
    const currentGuideRow = guideRows?.[verticalPosition];
    const guideCells = currentGuideRow?.guideCells;
    const selectedGuideCell = guideCells?.[horizontalPosition - 1];
    const shouldHideMetadata = selectedGuideCell?.isAdult && mShouldHideMetadataRef?.current;
    const selectedCell = shouldHideMetadata
        ? {
              ...selectedGuideCell,
              title: Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN),
              isNew: false,
          }
        : selectedGuideCell;
    const offerId = selectedGuideCell?.offerId;

    const services = React.useMemo(() => new VodInteractor(), []);
    const primaryBrandingImageUrl = services.getPartnerBrandingImageUrl(
        PRIMARY_BRANDING_LOGO_WIDTH_1080P,
        PRIMARY_BRANDING_LOGO_HEIGHT_1080P,
    );

    const dispatch = useTivoDispatch();
    const updateUIOnDataStateChange = useCallback(
        (state: UIOrchestratorState) => {
            switch (state) {
                case UIOrchestratorState.DATA_LOADED:
                    dispatch(guideHideSpinner());
                    break;
                case UIOrchestratorState.FETCHING_DATA:
                    dispatch(guideShowSpinner());
                    break;
                case UIOrchestratorState.ONE_PASS_CREATED:
                case UIOrchestratorState.ONE_PASS_DELETED:
                case UIOrchestratorState.ONE_PASS_MODIFIED:
                case UIOrchestratorState.RECORDING_CREATED:
                case UIOrchestratorState.RECORDING_DELETED:
                case UIOrchestratorState.RECORDING_MODIFIED:
                    dispatch(getRecordingsStatus());
                    break;
                default:
            }
        },
        [dispatch],
    );

    const isNetworkDisconnected = NetworkManager.isNetworkDisconnected();

    useEffect(() => {
        // Hide overlay and spinner in case it is still visible from sign-in screen
        dispatch(hideSpinner());
        if (isOverlayDialogActive) {
            dispatch(hideOverlayDialog());
            dispatch(clearOverlayTaskController());
            dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_GUIDE_TASKS));
        }

        Diagnostics.info(TAG, 'GuideScreen mounted.');
        Log.i(TAG, 'GuideScreen mounted.');
        VuLogger.logGuideScreenEntryEvent();

        if (isNetworkDisconnected) {
            dispatch(
                showWhisper({
                    message: NetworkManager.getNetworkDisconnectedMessage(),
                    iconType: WhisperTypes.INFO,
                }),
            );
        } else if (recordingsStatusMap?.size === 0) dispatch(getRecordingsStatus());

        // Subscribe to events and state changes
        mShouldHideMetadataRef.current = services.shouldHideMetadata();
        UIOrchestrator.stateChangeListener.subscribe(updateUIOnDataStateChange);
        VideoPlaybackController.getInstance()
            .getReadyListener()
            .subscribe(handleOnVideoControllerReady);
        const cursorManagerRef = CursorManager.subscribe(onCursorStateChangeCallback);

        // Initialize grid guide funcationalities
        if (!DeviceModel.getAppSettings().isVideoWindowEnabled()) enablePosterImage.current = true;
        dispatch(setChannelOptions());
        dispatch(loadGridGuide());

        // Ensure that key ignoring is stopped when the guide is loaded
        const keyManagerIns: KeyManager = KeyManager.getInstance();
        if (keyManagerIns.isKeyIgnoring()) {
            keyManagerIns.stopKeyIgnoring();
        }

        return () => {
            dispatch(resetGridGuideRowsAction());
            dispatch(resetGridGuideChannelsAction());
            UIOrchestrator.stateChangeListener.cancel(updateUIOnDataStateChange);
            VuLogger.logGuideScreenExitEvent();
            CursorManager.unsubscribe(cursorManagerRef);
            VideoPlaybackController.getInstance()
                .getReadyListener()
                .cancel(handleOnVideoControllerReady);
            Diagnostics.info(TAG, 'GuideScreen unmounted.');
            Log.i(TAG, 'GuideScreen unmounted.');
        };
    }, []);

    useEffect(() => {
        mShouldHideMetadataRef.current = services.shouldHideMetadata();
    }, [pcState]);

    useEffect(() => {
        let recordingsStatusTimerId: NodeJS.Timeout;
        if (maxAge) {
            recordingsStatusTimerId = setTimeout(() => {
                dispatch(getRecordingsStatus());
            }, maxAge * 1000);
        }

        return () => {
            clearTimeout(recordingsStatusTimerId);
        };
    }, [recordingsStatusMap]);

    useEffect(() => {
        const hasOnlyOneGuideCell = guideCells?.length === 1;
        if (hasOnlyOneGuideCell && horizontalPosition > 0) {
            dispatch(setHorizontalPositionAction(1));
        }
        const sPosterImageUrl = selectedGuideCell?.posterImageUrl;
        const fetchOfferPreviewTimeoutId = setTimeout(() => {
            setPosterImageUrl(sPosterImageUrl);

            if (shouldHideMetadata) setIsAdultPosterImage(true);
            else setIsAdultPosterImage(false);

            fetchOfferPreview();
        }, GUIDE_NETWORK_CALL_WAIT_DURATION);

        return () => {
            clearTimeout(fetchOfferPreviewTimeoutId);
        };
    }, [guideRows, horizontalPosition, verticalPosition, pcState]);

    const prevVerticalPageCountRef = useRef<number | null>(null);
    useEffect(() => {
        const prev = prevVerticalPageCountRef.current;

        // Initialize prev on first mount and skip running the effect
        if (prev === null) {
            prevVerticalPageCountRef.current = verticalPageCount;
            // return a no-op cleanup to keep return type consistent
            return () => {};
        }

        // No change — skip work
        if (prev === verticalPageCount) {
            // return a no-op cleanup to keep return type consistent
            return () => {};
        }

        // verticalPageCount changed — perform work
        let guidePageTimeoutId: NodeJS.Timeout | null = null;
        guidePageTimeoutId = setTimeout(() => {
            dispatch(loadGridGuidePastRows());
            dispatch(loadGridGuideRows());
            dispatch(loadGridGuideFutureRows());
        }, GUIDE_NETWORK_CALL_WAIT_DURATION);

        // update previous value for next run
        prevVerticalPageCountRef.current = verticalPageCount;

        return () => {
            if (guidePageTimeoutId !== null) {
                clearTimeout(guidePageTimeoutId);
            }
        };
    }, [verticalPageCount]);

    useEffect(() => {
        const timerId = setInterval(() => {
            const currentTime = new DateTime();
            const endTime = startTime.addMinutes(IDLE_GRID_GUIDE_DURATION);
            if (
                currentTime.getTime() > endTime.getTime() ||
                currentTime.getTime() < startTime.getTime()
            ) {
                dispatch(refreshGridGuide());
            }
        }, GRID_TIMEBAR_REFRESH_TIME);

        return () => {
            if (timerId) clearInterval(timerId);
        };
    }, [startTime]);

    useEffect(() => {
        const hasValidGuideCells = guideCells?.length;
        if (hasValidGuideCells && horizontalPosition > 0) {
            let currentIndex = evaluateCurrentHorizontalSessionIndex(
                guideCells,
                currentTimestamp,
                horizontalSession,
            );
            currentIndex = currentIndex === -1 ? 0 : currentIndex;
            dispatch(setHorizontalPosition(currentIndex + 1));
        }
    }, [guideRows]);

    useEffect(() => {
        let turboScrollTimerId: any = null;
        let guidePageScrollTimerId: any = null;
        if (isTurboScrollActive) {
            turboScrollTimerId = setTimeout(() => {
                dispatch(fetchAndUpdateChannels());
            }, TURBO_SCROLL_ANIMATION_DURATION);
        }
        if (isGuidePageScrollActive) {
            guidePageScrollTimerId = setTimeout(() => {
                dispatch(fetchAndUpdateChannels());
            }, TURBO_SCROLL_ANIMATION_DURATION);
        }
        return () => {
            clearTimeout(turboScrollTimerId);
            clearTimeout(guidePageScrollTimerId);
        };
    }, [isTurboScrollActive, channels, isGuidePageScrollActive]);

    useEffect(() => {
        if (isScreenActive?.current) {
            if (visibilityState === VisibilityState.ON_PAUSE) {
                VuLogger.logGuideScreenPauseEvent();
            }
            if (visibilityState === VisibilityState.ON_RESUME) {
                VuLogger.logGuideScreenResumeEvent();
            }
        }
        return () => {
            isScreenActive.current = true;
        };
    }, [visibilityState]);

    useEffect(() => {
        if (isNetworkDisconnected && isGuideSpinnerActive) {
            dispatch(guideHideSpinner());
        }
    }, [isGuideSpinnerActive]);

    const generateGridGuideRows = (): TGridGuideRow[] => {
        const isValidGridGuideRows =
            guideRows.length > 0 && pastGuideRows.length > 0 && futureGuideRows.length > 0;
        if (!isValidGridGuideRows) return [];

        let sGuideRows = guideRows.map((guideRow: TGridGuideRow) => ({
            stationId: guideRow.stationId,
            guideCells: guideRow.guideCells.map((cell: TGuideCell) => ({
                ...cell,
            })) as TGuideCell[],
        }));

        sGuideRows = sGuideRows.map((sGuideRow: TGridGuideRow, index) => {
            const aPastGuideRow: TGridGuideRow = pastGuideRows[index];
            const aPastGuideRowCells: TGuideCell[] = aPastGuideRow?.guideCells;
            const aPastGuideRowLastCell: TGuideCell =
                aPastGuideRowCells[aPastGuideRowCells?.length - 1];
            const aFutureGuideRow: TGridGuideRow = futureGuideRows[index];
            const aFutureGuideRowCells: TGuideCell[] = aFutureGuideRow?.guideCells;
            const aFutureGuideRowFirstCell: TGuideCell = aFutureGuideRowCells[0];
            const aGuideRow = sGuideRow;
            aGuideRow.guideCells.unshift(aPastGuideRowLastCell);
            const lastIndex = aGuideRow.guideCells.length - 1;
            const mStartTime = aGuideRow.guideCells[lastIndex].startTime;
            const endTime = mStartTime.addSeconds(aGuideRow.guideCells[lastIndex].duration);
            if (endTime.getTime() <= nextTimestamp.getTime())
                aGuideRow.guideCells.push(aFutureGuideRowFirstCell);
            return aGuideRow;
        });

        return sGuideRows;
    };

    const mGridGuideRows: TGridGuideRow[] = useMemo(
        () => generateGridGuideRows(),
        [guideRows, pastGuideRows, futureGuideRows, nextTimestamp],
    );

    const fetchOfferPreview = () => {
        NFRLoggings.log(NFREvents.START_LOADING_PREVIEW);
        const networkCallId = UniqueIdentifier.getNextGuidePreviewNetworkCallId();
        if (offerId) {
            setMOfferPreview({});

            services
                .fetchOfferPreview(offerId, ScreenType.GuideHeader, networkCallId)
                .then((response: TOfferPreview) => {
                    let offerPreview = {};
                    if (
                        response.networkCallId === UniqueIdentifier.getGuidePreviewNetworkCallId()
                    ) {
                        if (Object.keys(response.offerPreview).length > 0) {
                            offerPreview = response.offerPreview;
                            dispatch(setGridGuideOfferPreviewDataAction(offerPreview, offerId));
                        } else {
                            offerPreview = generateOfferPreviewData();
                        }
                        setMOfferPreview({ ...offerPreview });
                    }
                    NFRLoggings.log(NFREvents.FINISH_LOADING_PREVIEW);
                })
                .catch((previewError) => {
                    const guidePreviewError = previewError?.response?.data;
                    const serviceError = new PreviewError(
                        guidePreviewError?.message,
                        guidePreviewError?.code,
                    );
                    VuLogger.logAppError(serviceError);
                    OperationsLogger.logUIErrorCode(serviceError.getCode());
                    const offerPreview = generateOfferPreviewData();
                    setMOfferPreview(offerPreview);
                    dispatch(setGridGuideOfferPreviewDataAction(offerPreview, offerId));
                    Diagnostics.error(TAG, 'Failed to fetch offer preview data', previewError);
                    Log.e(TAG, `OfferPreview: ${previewError}`);
                    NFRLoggings.log(NFREvents.FAILURE_LOADING_PREVIEW, serviceError);
                });
        } else if (horizontalPosition === 0) {
            const hasChannel = channels.length > 0 && channels[verticalPosition];
            const channel = channels[verticalPosition];
            setMOfferPreview({
                contentPreview: {
                    title: hasChannel ? `${channel.callSign} ${channel.channelNumber}` : '',
                    streamType: hasChannel ? channel.videoResolution : '',
                    description: hasChannel ? channel.affiliate ?? channel.name : '',
                },
            });
        } else {
            setMOfferPreview({ ...generateOfferPreviewData() });
        }
    };

    const generateOfferPreviewData = (): object => {
        if (!guideCells) {
            return {};
        }
        const programTitle: string = shouldHideMetadata
            ? Locale.getString(LocaleKeys.GUIDE_TITLE_HIDDEN)
            : selectedGuideCell?.title ?? '';

        const programDescription = shouldHideMetadata
            ? Locale.getString(LocaleKeys.GUIDE_DESCRIPTION_HIDDEN)
            : Locale.getString(LocaleKeys.DESCRIPTION_NOT_AVAILABLE);
        const movieYear = selectedGuideCell?.movieYear;
        const programContentType: string = selectedGuideCell?.contentType ?? '';
        const previewType =
            programContentType === ContentType.Movie
                ? PreviewType.MoviePreview
                : programContentType;

        const offerPreviewData = {
            contentPreview: {
                title: programTitle,
                description: programDescription,
                movieYear,
                previewType,
            },
        };
        return offerPreviewData;
    };

    const onCursorStateChangeCallback = useCallback(
        (state) => {
            if (state === false) {
                dispatch(guideSetCaretInvisible());
            }
        },
        [dispatch],
    );

    const handleOnVideoControllerReady = useCallback(async () => {
        try {
            if (DeviceModel.getAppSettings().isVideoWindowEnabled()) {
                const getPlayable = isJumpChannelCcbSwitchEnabled()
                    ? services.getLastPlayedOrFallbackToFirstByChannelNumberV2
                    : services.getLastPlayedOrFallbackToFirstByChannelNumber;
                const playable = await getPlayable(
                    DeviceModel.getAppSettings().getLastTunedChannelNumber(),
                );
                VideoPlaybackController.getInstance().startPlayback(playable);
            }
        } catch (error) {
            Log.d(TAG, 'handleOnVideoControllerReady ', error);
        }
    }, [services]);

    return (
        <React.Fragment>
            {isGuideSpinnerActive ? null : <NoNetworkOverlay />}
            <SidebarNavigationMenu />
            <div id="guide" className="grid-guide-main" data-testid="guide">
                <Mousetrap className="top-section" value={''}>
                    <div
                        className={`preview-pane ${isAdultPosterImage ? 'adjust-padding' : ''}`}
                        data-testid="guide-preview-pane"
                    >
                        <div className="top-row">
                            <ImageLoader
                                src={primaryBrandingImageUrl}
                                imageType={ImageLoaderTypes.PARTNER_IMAGE_PRIMARY_BRANDING}
                                dataTestId="guide-primary-branding"
                            />
                            <Clock type={ClockTypes.GUIDE_CLOCK} />
                        </div>
                        {!isChannelsServiceFail &&
                        !isTurboScrollActive &&
                        !isGuidePageScrollActive ? (
                            <PreviewPane
                                offerPreview={mOfferPreview}
                                hideMetadata={mShouldHideMetadataRef.current}
                                channel={channels[verticalPosition]}
                                selectedCell={selectedCell}
                                horizontalPosition={horizontalPosition}
                                verticalPosition={verticalPosition}
                            />
                        ) : null}
                    </div>
                    <PosterImage
                        imageUrl={enablePosterImage.current ? posterImageUrl : ''}
                        isAdult={isAdultPosterImage}
                    />
                </Mousetrap>
                <div className="container-white">
                    <div className="container-guide">
                        <Mousetrap value={''}>
                            <GridGuideHeader />
                        </Mousetrap>
                        {isPastGuideMode ? (
                            <PastGuide
                                limit={LIMIT}
                                channels={channels}
                                guideRows={mGridGuideRows}
                                currentTimestamp={currentTimestamp}
                                nextTimestamp={nextTimestamp}
                                visibilityState={visibilityState}
                                hideMetadata={mShouldHideMetadataRef.current}
                                recordingsStatusMap={recordingsStatusMap}
                            />
                        ) : (
                            <MainGuide
                                limit={LIMIT}
                                channels={channels}
                                guideRows={mGridGuideRows}
                                currentTimestamp={currentTimestamp}
                                nextTimestamp={nextTimestamp}
                                visibilityState={visibilityState}
                                hideMetadata={mShouldHideMetadataRef.current}
                                recordingsStatusMap={recordingsStatusMap}
                                isGuideRowsServiceFail={isGuideRowsServiceFail}
                            />
                        )}
                    </div>
                    {isTurboScrollActive || isGuidePageScrollActive ? (
                        <div
                            className={`turbo-scroll-container${
                                (isPastGuideMode && ' past-guide-mode') || ''
                            }`}
                            data-testid="turbo-scroll"
                        >
                            <TurboScroll
                                channelNumber={channels[verticalPosition]?.channelNumber ?? ''}
                            />
                        </div>
                    ) : null}
                </div>
                <GridGuideFooter
                    selectedCategoryType={Locale.getString(categoriesTypeIndex)}
                    selectedChannelType={Locale.getString(channelsTypeIndex)}
                    highlightId={AppUtils.createHighlightId(horizontalPosition, verticalPosition)}
                    optionsMessage={
                        horizontalPosition === 0
                            ? Locale.getString(LocaleKeys.GUIDE_SELECT_OPTIONS_MESSAGE)
                            : Locale.getString(LocaleKeys.GUIDE_SELECT_PRESS_AND_HOLD_MESSAGE)
                    }
                    settingsMessageFirstPart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_FIRST_PART,
                    )}
                    settingsMessageMiddlePart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_MIDDLE_PART,
                    )}
                    settingsMessageLastPart={Locale.getString(
                        LocaleKeys.GUIDE_SETTINGS_MESSAGE_LAST_PART,
                    )}
                    menuMessage={Locale.getString(LocaleKeys.GUIDE_FOOTER_MENU_TEXT)}
                />
                <Whisper message="" />
            </div>
            {isAntiburnOsdActive && <AntiburnOsd parentalCheck={false} />}
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
            {isSpinnerActive ? (
                <div className="grid-guide-spinner">
                    <Spinner message="" />
                </div>
            ) : null}
        </React.Fragment>
    );
};

export default Guide;
